import { Invoice, InvoiceBody, InvoiceItem, Product } from "@lib/types/Billing";
import { useState, useEffect, useMemo } from "react";
import { itemTotalPrice, priceTimesQuantity } from "./utils";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { Customer } from "@lib/types/Customer";

type Props = {
  invoiceQuery: UseQueryProps<Invoice>;
  productsQuery: UseQueryProps<PaginationType<Product>>;
  setSelectedInvoice: (invoice?: Invoice) => void;
  customer?: Customer;
};

export const useInvoice = ({
  invoiceQuery,
  productsQuery,
  setSelectedInvoice,
  customer,
}: Props) => {
  const initialRequestBody: InvoiceBody = {
    customerId: customer?.id || "",
    dueDate: null,
    products: [],
    description: "",
  };

  const invoiceData = invoiceQuery?.data?.data;
  const products = productsQuery?.data?.data?.data || [];

  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [requestBody, setRequestBody] = useState<InvoiceBody>();
  const [makingClone, setMakingClone] = useState<boolean>(false);

  const itemsTotalAmount = useMemo(() => {
    let total = 0;
    for (let index = 0; index < items?.length; index++) {
      total += itemTotalPrice(items[index]);
    }
    return total || 0;
  }, [items]);
  const itemsTotalDiscount = useMemo(() => {
    let total = 0;
    for (let index = 0; index < items?.length; index++) {
      total += items[index]?.discount || 0;
    }
    return total || 0;
  }, [items]);
  const itemsTotalWithoutDiscountAmount = useMemo(() => {
    let total = 0;
    for (let index = 0; index < items?.length; index++) {
      total += priceTimesQuantity(items[index]);
    }
    return total || 0;
  }, [items]);

  useEffect(() => {
    if (invoiceData) {
      setItems(
        invoiceData?.orders?.map((order) => ({
          id: order?.productId,
          unitPrice: order?.unitPrice,
          discount: order?.discount || 0,
          quantity: order?.quantity || 1,
        })),
      );
      setRequestBody({
        id: invoiceData?.id,
        customerId: invoiceData?.customer?.id,
        dueDate: invoiceData?.dueDate,
        products: items,
        description: invoiceData?.description,
        status: invoiceData?.status,
      });
    } else {
      if (makingClone) {
        setRequestBody({
          customerId: customer?.id || "",
          ...requestBody,
          products: items,
          id: "",
          status: undefined,
        });

        setMakingClone(false);
      } else {
        setItems([]);
        setRequestBody(initialRequestBody);
      }
    }
  }, [invoiceData, customer]);

  const handleCloneInvoice = () => {
    if (!invoiceData) return false;

    setMakingClone(true);
    setSelectedInvoice();
  };

  const handleRequestBody = (props: Partial<InvoiceBody>) => {
    setRequestBody({
      ...requestBody,
      ...props,
    });
  };

  useEffect(() => {
    handleRequestBody({ products: items || [] });
  }, [items]);

  const onChangeLineItems = (
    index: number,
    key: keyof InvoiceItem,
    value: string | number,
  ) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [key]: value };

    if (key == "id") {
      newItems[index].unitPrice = Number(
        products.find((p) => p.id === value)?.unitPrice || 0,
      );
    }

    setItems(newItems);
  };

  const onDeleteLineItem = (index: number) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  return {
    items,
    initialRequestBody,
    requestBody,
    itemsTotalAmount,
    itemsTotalDiscount,
    itemsTotalWithoutDiscountAmount,
    onChangeLineItems,
    onDeleteLineItem,
    invoiceData,
    handleRequestBody,
    setItems,
    handleCloneInvoice,
  };
};
